/**
 * AnimatedCollapse Component
 *
 * Wrapper component that provides smooth expand/collapse animations
 * using CSS grid technique for height transitions.
 */

import { useRef, useLayoutEffect, useState } from 'react';
import { cn } from '../../../../lib/utils';

type AnimatedCollapseProps = {
  isOpen: boolean;
  children: React.ReactNode;
  className?: string;
};

export function AnimatedCollapse({
  isOpen,
  children,
  className,
}: AnimatedCollapseProps) {
  const isTestMode =
    typeof window !== 'undefined' &&
    window.electronAPI?.testMode?.isTestMode?.() === true;

  // E2E runs benefit from deterministic layout without animated height transitions.
  if (isTestMode) {
    return (
      <div className={cn(!isOpen && 'hidden', className)} aria-hidden={!isOpen}>
        {children}
      </div>
    );
  }

  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | 'auto'>(isOpen ? 'auto' : 0);
  const [isAnimating, setIsAnimating] = useState(false);
  const hasMountedRef = useRef(false);
  const heightRef = useRef(height);
  heightRef.current = height;

  useLayoutEffect(() => {
    if (!contentRef.current) return;

    // Avoid running open/close animations on first mount.
    // This prevents "collapsed but temporarily visible" states that can block clicks.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      setIsAnimating(false);
      setHeight(isOpen ? 'auto' : 0);
      return;
    }

    if (isOpen) {
      // Expanding: measure content, animate to that height, then set to auto
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(contentHeight);
      setIsAnimating(true);

      const timeout = setTimeout(() => {
        setHeight('auto');
        setIsAnimating(false);
      }, 200); // Match transition duration

      return () => clearTimeout(timeout);
    } else {
      // Collapsing: set explicit height first, then animate to 0
      if (heightRef.current === 'auto') {
        const contentHeight = contentRef.current.scrollHeight;
        setHeight(contentHeight);
        // Force reflow
        contentRef.current.offsetHeight;
      }
      setIsAnimating(true);
      requestAnimationFrame(() => {
        setHeight(0);
      });

      const timeout = setTimeout(() => {
        setIsAnimating(false);
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  return (
    <div
      ref={contentRef}
      className={cn(
        'overflow-hidden transition-[height,opacity] duration-200 ease-in-out',
        !isOpen && !isAnimating && 'hidden',
        isOpen && height === 0 && !isAnimating && 'invisible',
        className
      )}
      style={{ height: height === 'auto' ? 'auto' : `${height}px` }}
      aria-hidden={!isOpen}
    >
      {children}
    </div>
  );
}
